import * as yup from 'yup'
import i18n from '@/configs/i18n'

// Helper to get translated validation message
const t = (key: string) => i18n.t(key, { ns: 'Contract' })

export const getProductSchema = (product: any) => {
    // Route domain services by template_name (exact key: "Domain.TLDs" | "Domain.VNNIC")
    // to avoid applying VNNIC declaration requirements to international TLDs.
    const templateName = product?.template_name as string | undefined
    if (templateName === 'Domain.TLDs') return DomainTLDsValidator
    if (templateName === 'Domain.VNNIC') return DomainVNNICValidator
    if (
        templateName === 'Other.Standard' ||
        product?.product_group_code === 'OTHER'
    )
        return OtherStandardValidator

    switch (product?.product_group_name?.toLocaleLowerCase()) {
        case 'content delivery network':
        case 'storage':
            return CDNServiceValidator
        case 'transcoding':
        case 'recording':
        case 'transmuxing':
            return TranscodingTemplateValidator
        case 'hosting':
        case 'cloud server':
        case 'vnis':
            return HostingServiceValidator
        case 'bare metal server/ dedicated server':
        case 'dedicated server':
            return DedicatedServerValidator

        case 'domain':
            // Fallback when template_name absent: use VNNIC (stricter) to avoid bypassing validation.
            return DomainVNNICValidator
        case 'cloud service':
            return VCloudServiceValidator
        case 'ssl':
            return SSLServiceValidator

        default:
            return yup.object().shape({
                product_group_name: yup.string().required(),
                product_id: yup.number().min(1).required(),
            })
    }
}

const contractsNewSchema = yup.object().shape({
    contract_expiry_date: yup.date().required(),
    contract_effective_date: yup.date().required(),
    contract_role: yup
        .string()
        .oneOf([
            'customer',
            'supplier',
            'reseller',
            'affiliate',
            'collaborator',
        ])
        .required(),
    contract_direction: yup.string().oneOf(['earn', 'pay']).required(),
    customer_id: yup.number().min(1).required(),
    partner_id: yup.number().nullable(),
    order: yup.object().shape({
        products: yup
            .array()
            .of(
                yup.lazy((product) => {
                    return getProductSchema(product)
                })
            )
            .min(1),
    }),
})

const baseValidator = {
    product_id: yup.number().min(1).required(),
    product_group_name: yup.string().required(),
    product_name: yup.string().required(),
    service_price_model: yup.string().required(),
    service_times: yup.object().shape({
        first_startdate: yup.date().required(),
        // Both windows must move forward. A reversed one is read by the backend
        // as an empty span and billed as zero.
        first_enddate: yup
            .date()
            .required()
            .min(
                yup.ref('first_startdate'),
                t('validation.end_date_after_start_date')
            ),
        continue_startdate: yup.date().required(),
        continue_enddate: yup
            .date()
            .required()
            .min(
                yup.ref('continue_startdate'),
                t('validation.end_date_after_start_date')
            ),
        statement: yup.string().required(),
    }),
}

const CDNServiceValidator = yup.object().shape({
    ...baseValidator,
    product_form: yup.string().required(),
    service_configoptions: yup.array().of(
        yup.object().shape({
            price_config: yup.object().shape({
                method: yup.string().required(),
            }),
            commitment_prepaid: yup.boolean().required(),
            commitment_time: yup.number().optional().default(1),
            prices: yup
                .array()
                .optional()
                .of(
                    yup.object().shape({
                        price_per_unit: yup.number().min(1).required(),
                        price_start: yup.number().optional(),
                        price_end: yup.number().optional(),
                    })
                ),
            unit: yup.string().required(),
        })
    ),
})

const HostingServiceValidator = yup.object().shape({
    ...baseValidator,
    prepaid: yup.boolean().required(),
    service_configoptions: yup.array().of(
        yup.object().shape({
            commitment_prepaid: yup.boolean().required(),
            prices: yup
                .array()
                .optional()
                .of(
                    yup.object().shape({
                        price_per_unit: yup
                            .number()
                            .min(1)
                            .required('services.price_per_unit_required'),
                    })
                ),
            unit: yup.string().required(),
            name: yup.string().required(),
        })
    ),
    service_price_register: yup.number().optional().min(1),
})

const OtherStandardValidator = yup.object().shape({
    product_group_name: yup.string().required(),
    product_id: yup.number().min(1).required(),
    product_name: yup.string().required(),
    service_price_model: yup.string().required(),
    service_price_register: yup.number().min(1).required(),
    service_times: yup.object().shape({
        first_startdate: yup.date().required(),
        first_enddate: yup
            .date()
            .required()
            .min(
                yup.ref('first_startdate'),
                t('validation.end_date_after_start_date')
            ),
    }),
    service_domain_metadata: yup.object().shape({
        other_standard_payment_schedule: yup.object().shape({
            payment_method: yup
                .string()
                .oneOf(['prepaid', 'postpaid'])
                .required(),
            payment_mode: yup
                .string()
                .oneOf(['one_time', 'multiple_times'])
                .required(),
            description: yup.string().optional(),
            ranges: yup
                .array()
                .of(
                    yup.object().shape({
                        start_date: yup.date().required(),
                        end_date: yup
                            .date()
                            .min(
                                yup.ref('start_date'),
                                t('validation.end_date_after_start_date')
                            )
                            .required(),
                        price: yup.number().min(1).required(),
                    })
                )
                .min(1)
                .required(),
        }),
    }),
})

// Dedicated/Bare Metal Server - validates warehouse config options
const DedicatedServerValidator = yup.object().shape({
    ...baseValidator,
    prepaid: yup.boolean().required(),
    service_configoptions: yup.array().of(
        yup.lazy((option) => {
            // Warehouse/server-warehouse type requires capacity_value, brand_name, model_name
            if (
                option?.type === 'warehouse' ||
                option?.type === 'server-warehouse'
            ) {
                return yup.object().shape({
                    commitment_prepaid: yup.boolean().required(),
                    prices: yup
                        .array()
                        .optional()
                        .of(
                            yup.object().shape({
                                price_per_unit: yup
                                    .number()
                                    .min(1)
                                    .required(
                                        'services.price_per_unit_required'
                                    ),
                                commitment_price_per_unit: yup
                                    .number()
                                    .min(1)
                                    .required(
                                        'services.commitment_price_required'
                                    ),
                            })
                        ),
                    unit: yup.string().required(),
                    name: yup.string().required(),
                    type: yup.string().required(),
                    capacity_value: yup
                        .number()
                        .min(1)
                        .required('services.capacity_value_required'),
                    brand_name: yup
                        .string()
                        .required('services.brand_name_required'),
                    model_name: yup
                        .string()
                        .required('services.model_name_required'),
                })
            }
            // Default validation for other types
            return yup.object().shape({
                commitment_prepaid: yup.boolean().required(),
                prices: yup
                    .array()
                    .optional()
                    .of(
                        yup.object().shape({
                            price_per_unit: yup
                                .number()
                                .min(1)
                                .required('services.price_per_unit_required'),
                            commitment_price_per_unit: yup
                                .number()
                                .min(1)
                                .required('services.commitment_price_required'),
                        })
                    ),
                unit: yup.string().required(),
                name: yup.string().required(),
            })
        })
    ),
    service_price_register: yup.number().optional().min(1),
})

const domainRegex =
    /^(?=.{4,253}$)(?!-)(?:[a-zA-Z0-9-]{1,63}\.)+[a-zA-Z]{2,63}$/

// International TLDs (.com/.net/.org/...) — no VN declaration required.
const DomainTLDsValidator = yup.object().shape({
    ...baseValidator,
    service_domain_metadata: yup.object().shape({
        domain_type: yup.string().required(),
        domain_value: yup
            .string()
            .required(t('validation.domain_value_required'))
            .matches(domainRegex, t('validation.domain_value_invalid')),
        domain_registrationperiod: yup.string().required(),
        domain_eppcode: yup.string().when('domain_type', {
            is: 'transfer',
            then: (shema) => shema.required(),
        }),
        dns_option: yup.string().optional(),
    }),
    prepaid: yup.boolean().required(),
    service_price_register: yup.number().min(1).required(),
})

// VNNIC (.vn) — full declaration fields required by VNNIC registrar.
const DomainVNNICValidator = yup.object().shape({
    ...baseValidator,
    service_domain_metadata: yup.object().shape({
        domain_type: yup.string().required(),
        domain_value: yup
            .string()
            .required(t('validation.domain_value_required'))
            .matches(domainRegex, t('validation.domain_value_invalid')),
        domain_registrationperiod: yup.string().required(),
        domain_eppcode: yup.string().when('domain_type', {
            is: 'transfer',
            then: (shema) => shema.required(),
        }),
        dns_option: yup.string().optional(),
        domain_extra_fields: yup.object().shape({
            for: yup.string().required(),
            ownerName: yup
                .string()
                .required(t('validation.ownerName_required')),
            ownerProvince: yup.object().shape({
                value: yup
                    .string()
                    .required(t('validation.ownerProvince_required')),
                label: yup.string().required(),
            }),
            ownerDistrict: yup.object().shape({
                value: yup
                    .string()
                    .required(t('validation.ownerDistrict_required')),
                label: yup.string().required(),
            }),
            ownerWard: yup.object().shape({
                value: yup
                    .string()
                    .required(t('validation.ownerWard_required')),
                label: yup.string().required(),
            }),
            ownerAddress: yup
                .string()
                .required(t('validation.ownerAddress_required')),
            ownerCountry: yup.object().shape({
                value: yup
                    .string()
                    .required(t('validation.ownerCountry_required')),
                label: yup.string().required(),
            }),
            ownerEmail1: yup
                .string()
                .email()
                .required(t('validation.ownerEmail1_required')),
            ownerPhone: yup
                .string()
                .required(t('validation.ownerPhone_required')),
            ownerFax: yup.string().required(t('validation.ownerFax_required')),
            ownerID_Number: yup.string().when('for', {
                is: 'canhan',
                then: (shema) => shema.required(),
            }),
            ownerTaxCode: yup.string().when('for', {
                is: 'congty',
                then: (shema) => shema.required(),
            }),
            uiName: yup.string().required(t('validation.uiName_required')),
            uiProvince: yup.object().shape({
                value: yup
                    .string()
                    .required(t('validation.uiProvince_required')),
                label: yup.string().required(),
            }),
            uiDistrict: yup.object().shape({
                value: yup
                    .string()
                    .required(t('validation.uiDistrict_required')),
                label: yup.string().required(),
            }),
            uiWard: yup.object().shape({
                value: yup.string().required(t('validation.uiWard_required')),
                label: yup.string().required(),
            }),
            uiAddress: yup
                .string()
                .required(t('validation.uiAddress_required')),
            uiCountry: yup.object().shape({
                value: yup
                    .string()
                    .required(t('validation.uiCountry_required')),
                label: yup.string().required(),
            }),
            uiEmail: yup
                .string()
                .email()
                .required(t('validation.uiEmail_required')),
            uiPhone: yup.string().required(t('validation.uiPhone_required')),
            uiGender: yup.string().when('for', {
                is: 'canhan',
                then: (shema) =>
                    shema.required(t('validation.uiGender_required')),
            }),
            uiFax: yup.string().required(t('validation.uiFax_required')),
            uiBirthdate: yup.date().when('for', {
                is: 'canhan',
                then: (shema) => shema.required(),
            }),
            uiID_Number: yup.string().when('for', {
                is: 'canhan',
                then: (shema) => shema.required(),
            }),
            uiTaxCode: yup.string().when('for', {
                is: 'congty',
                then: (shema) => shema.required(),
            }),
            adminName: yup
                .string()
                .required(t('validation.adminName_required')),
            adminID_Number: yup
                .string()
                .required(t('validation.adminID_Number_required')),
            adminProvince: yup.object().shape({
                value: yup
                    .string()
                    .required(t('validation.adminProvince_required')),
                label: yup.string().required(),
            }),
            adminDistrict: yup.object().shape({
                value: yup
                    .string()
                    .required(t('validation.adminDistrict_required')),
                label: yup.string().required(),
            }),
            adminWard: yup.object().shape({
                value: yup
                    .string()
                    .required(t('validation.adminWard_required')),
                label: yup.string().required(),
            }),
            adminAddress: yup
                .string()
                .required(t('validation.adminAddress_required')),
            adminCountry: yup.object().shape({
                value: yup
                    .string()
                    .required(t('validation.adminCountry_required')),
                label: yup.string().required(),
            }),
            adminEmail: yup
                .string()
                .email()
                .required(t('validation.adminEmail_required')),
            adminPhone: yup
                .string()
                .required(t('validation.adminPhone_required')),
            adminGender: yup
                .string()
                .required(t('validation.adminGender_required')),
            adminFax: yup.string().required(t('validation.adminFax_required')),
            adminBirthdate: yup
                .date()
                .required(t('validation.adminBirthdate_required')),
        }),
    }),
    prepaid: yup.boolean().required(),
    service_price_register: yup.number().min(1).required(),
})

const VCloudServiceValidator = yup.object().shape({
    ...baseValidator,
    service_configoptions: yup.array().of(
        yup.object().shape({
            name: yup.string().required('services.vcloud.required_name'),
            commitment_value: yup
                .number()
                .min(1)
                .required('services.vcloud.commitment_value'),
        })
    ),
})

const TranscodingTemplateValidator = yup.object().shape({
    ...baseValidator,
    product_form: yup.string().required(),
    service_configoptions: yup
        .array()
        .of(
            yup.object().shape({
                price_config: yup.object().shape({
                    method: yup.string().required(),
                }),
                commitment_prepaid: yup.boolean().required(),
                commitment_time: yup.number().optional().default(1),
                prices: yup
                    .array()
                    .optional()
                    .of(
                        yup.object().shape({
                            price_per_unit: yup.number().min(1).required(),
                        })
                    ),
                unit: yup.string().required(),
            })
        )
        .max(1, 'Only one service configuration option is allowed'),
})

const SSLServiceValidator = yup.object().shape({
    ...baseValidator,
    service_price_register: yup.number().min(1).required(),
    service_domain_metadata: yup.object().shape({
        domain_value: yup.string().test({
            name: 'domain_value',
            test: (value) => {
                const regaxDomain =
                    /^(?=.{4,253}$)(?!\-)(?:[a-zA-Z0-9\-]{1,63}\.)+[a-zA-Z]{2,63}$/
                return regaxDomain.test(value as string)
            },
        }),
    }),
})
export default contractsNewSchema
